import React, { useState } from 'react';
import { Button, Textarea } from '@nextui-org/react';
import { Send, X, CornerDownRight } from 'lucide-react';

const TEAL = '#3FCED2';

interface ReplyComposerProps {
  /** Name des Autors, auf den geantwortet wird (für den Hinweis-Banner) */
  replyToName?: string | null;
  placeholder?: string;
  submitLabel?: string;
  autoFocus?: boolean;
  onSubmit: (content: string) => Promise<void>;
  onCancel?: () => void;
}

/**
 * Antwort-Composer (Port von forum_reply_modal.dart):
 * Textarea + "Antworten"-Button, mind. 3 Zeichen.
 */
const ReplyComposer: React.FC<ReplyComposerProps> = ({
  replyToName,
  placeholder = 'Schreibe eine Antwort...',
  submitLabel = 'Antworten',
  autoFocus = false,
  onSubmit,
  onCancel,
}) => {
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const trimmed = content.trim();
    if (trimmed.length < 3) {
      setError('Bitte mindestens 3 Zeichen eingeben.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(trimmed);
      setContent('');
    } catch (err) {
      // Fehlermeldung kommt vom Aufrufer als Error mit Message
      const msg =
        err instanceof Error && err.message
          ? err.message
          : 'Antwort konnte nicht gesendet werden';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3">
      {replyToName && (
        <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
          <CornerDownRight size={14} style={{ color: TEAL }} />
          <span>
            Antwort auf{' '}
            <span className="text-gray-200 font-medium">{replyToName}</span>
          </span>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="ml-auto text-gray-500 hover:text-white"
              aria-label="Antwort abbrechen"
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <Textarea
        value={content}
        onValueChange={setContent}
        placeholder={placeholder}
        minRows={2}
        maxLength={10000}
        autoFocus={autoFocus}
        classNames={{
          inputWrapper: 'bg-zinc-800 data-[hover=true]:bg-zinc-800',
          input: 'text-white',
        }}
      />

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      <div className="flex justify-end gap-2 mt-2">
        {onCancel && !replyToName && (
          <Button
            size="sm"
            variant="flat"
            className="bg-zinc-800 text-gray-300"
            onPress={onCancel}
            isDisabled={submitting}
          >
            Abbrechen
          </Button>
        )}
        <Button
          size="sm"
          style={{ backgroundColor: TEAL, color: '#000' }}
          className="font-semibold"
          startContent={!submitting ? <Send size={14} /> : undefined}
          onPress={handleSubmit}
          isLoading={submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

export default ReplyComposer;
